/**
 * HealthHome — /health
 * Landing for Health, Wellness & Medicine. Shows featured facility &
 * provider counts, browse-by-kind tiles, "Near You" strip (locality
 * aware), curated topic tiles that fan out to AI-generated articles.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  HeartPulse, Building2, Stethoscope, Pill, UserRound, ScanLine, Beaker,
  BookOpenText, Leaf, Brain, Sparkles, MapPin, ArrowRight, ShieldAlert,
} from "lucide-react";
import { http } from "@/lib/api";
import HealthCategoryNav from "@/components/HealthCategoryNav";
import TopicNewsStrip from "@/components/TopicNewsStrip";
import ArticlesOfInterest from "@/components/ArticlesOfInterest";
import FoodPulseHealthStrip from "@/components/FoodPulseHealthStrip";
import SeoHead from "@/components/SeoHead";
import SectionTagline from "@/components/SectionTagline";
import { useLocality } from "@/contexts/LocalityContext";

const KIND_ICONS = {
  disease:     HeartPulse,
  symptom:     ShieldAlert,
  procedure:   ScanLine,
  drug:        Beaker,
  wellness:    Leaf,
  mental:      Brain,
  traditional: Sparkles,
};

export default function HealthHome() {
  const [home, setHome] = useState({ counts: {}, featured_facilities: [], featured_providers: [], seed_topics: [] });
  const [meta, setMeta] = useState(null);
  const [near, setNear] = useState({ facilities: [], providers: [] });
  const { locality } = useLocality();

  useEffect(() => {
    (async () => {
      const [h, m] = await Promise.all([
        http.get("/health/home").then(r => r.data).catch(() => ({})),
        http.get("/health/meta").then(r => r.data).catch(() => null),
      ]);
      setHome({ counts: {}, featured_facilities: [], featured_providers: [], seed_topics: [], ...h });
      setMeta(m);
    })();
  }, []);

  useEffect(() => {
    if (!locality?.city && !locality?.country) { setNear({ facilities: [], providers: [] }); return; }
    const params = { limit: 6 };
    if (locality?.country) params.country = locality.country;
    if (locality?.city) params.city = locality.city;
    (async () => {
      const [f, p] = await Promise.all([
        http.get("/health/facilities", { params }).then(r => r.data?.items || []).catch(() => []),
        http.get("/health/providers",  { params }).then(r => r.data?.items || []).catch(() => []),
      ]);
      setNear({ facilities: f, providers: p });
    })();
  }, [locality?.country, locality?.state, locality?.city]);

  const topicsByKind = useMemo(() => {
    const g = {};
    for (const t of (home.seed_topics || [])) (g[t.kind] = g[t.kind] || []).push(t);
    return g;
  }, [home.seed_topics]);

  return (
    <>
      <SeoHead section="health" url="https://socialpulse.com/health" />
      <HealthCategoryNav />
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid="health-home">

        {/* Latest Health news */}
        <TopicNewsStrip topic="Health" category="Health" testId="health-news-strip" />

        <section className="rounded-xl p-6 md:p-10 mb-8" style={{ background: "var(--wofy-surface)" }}>
          <div className="overline mb-2">SOCIAL PULSE HEALTH</div>
          <h1 className="font-display text-4xl sm:text-5xl leading-tight mb-3">
            Health, Wellness &amp; Medicine
          </h1>
          <SectionTagline section="health" className="mb-3 font-medium text-lg" />
          <p className="max-w-2xl opacity-80">
            A trusted, community-centered resource for health information, healthcare providers, wellness services, medical institutions, research and community support.
          </p>
          <div className="mt-4 flex flex-wrap gap-2 text-sm">
            <Link to="/health/hospitals"  className="wofy-btn-primary inline-flex items-center gap-1.5" data-testid="cta-find-hospital"><Building2 className="w-4 h-4"/>Find a hospital</Link>
            <Link to="/health/providers"  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border hover:bg-black/5" style={{ borderColor: "var(--wofy-border)" }} data-testid="cta-find-doctor"><UserRound className="w-4 h-4"/>Find a doctor</Link>
            <Link to="/health/diseases"   className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border hover:bg-black/5" style={{ borderColor: "var(--wofy-border)" }} data-testid="cta-browse-diseases"><HeartPulse className="w-4 h-4"/>Browse diseases</Link>
          </div>
          <div className="mt-4 text-xs opacity-70 inline-flex items-start gap-1.5" data-testid="health-disclaimer">
            <ShieldAlert className="w-3.5 h-3.5 mt-0.5"/>
            {meta?.disclaimer || "Educational only — not medical advice. Always consult a qualified healthcare professional."}
          </div>
        </section>

        {(locality?.city || locality?.country) && (
          <section className="mb-10" data-testid="health-near-you">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <div>
                <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>
                  <MapPin className="w-3 h-3 inline mr-1 -mt-0.5"/>
                  HEALTH NEAR YOU · {[locality.city, locality.state, locality.country].filter(Boolean).join(", ").toUpperCase()}
                </div>
                <h2 className="font-display text-2xl">Local hospitals, clinics &amp; pharmacies</h2>
              </div>
              <Link to={`/health/hospitals?${locality?.country ? `country=${encodeURIComponent(locality.country)}` : ""}${locality?.city ? `&city=${encodeURIComponent(locality.city)}` : ""}`}
                    className="text-sm wofy-link inline-flex items-center gap-0.5">See all local <ArrowRight className="w-3 h-3"/></Link>
            </div>
            {near.facilities.length === 0 ? (
              <div className="wofy-card !p-6 text-center opacity-80">
                No local facilities listed yet. <Link to="/health/hospitals/new" className="wofy-link">Register the first →</Link>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {near.facilities.slice(0, 6).map(f => <FacilityCard key={f.id} f={f} />)}
              </div>
            )}
          </section>
        )}

        {/* Browse by facility kind */}
        <section className="mb-10">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-2xl">Browse by kind</h2>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { to: "/health/hospitals",   label: "Hospitals",       count: home.counts?.hospitals,  icon: Building2 },
              { to: "/health/clinics",     label: "Clinics",         count: home.counts?.clinics,    icon: Stethoscope },
              { to: "/health/pharmacies",  label: "Pharmacies",      count: home.counts?.pharmacies, icon: Pill },
              { to: "/health/providers",   label: "Providers",       count: home.counts?.providers,  icon: UserRound },
              { to: "/health/diseases",    label: "Diseases",        count: home.counts?.topics,     icon: HeartPulse },
              { to: "/health/wellness",    label: "Wellness",        count: null,                    icon: Leaf },
            ].map(({ to, label, count, icon: Icon }) => (
              <Link key={to} to={to} className="wofy-card !p-4 hover:shadow-md transition flex items-center gap-3"
                    data-testid={`browse-${label.toLowerCase()}`}>
                <div className="w-10 h-10 rounded-lg bg-black/5 grid place-items-center"><Icon className="w-5 h-5"/></div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{label}</div>
                  {count != null && <div className="text-xs opacity-70">{count} listed</div>}
                </div>
                <ArrowRight className="w-4 h-4 opacity-60"/>
              </Link>
            ))}
          </div>
        </section>

        {/* Curated topics — AI generates full article on first visit */}
        {["disease","symptom","procedure","drug","wellness","mental","traditional"].map(kind => {
          const items = topicsByKind[kind] || [];
          if (items.length === 0) return null;
          const Icon = KIND_ICONS[kind] || HeartPulse;
          const label = ({disease:"Diseases & Conditions", symptom:"Symptoms Guide", procedure:"Medical Procedures", drug:"Drug Information", wellness:"Wellness", mental:"Mental Health", traditional:"Traditional Medicine"})[kind];
          return (
            <section key={kind} className="mb-10">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display text-2xl inline-flex items-center gap-2"><Icon className="w-5 h-5"/>{label}</h2>
                <Link to={`/health/${kind === 'disease' ? 'diseases' : kind === 'symptom' ? 'symptoms' : kind === 'procedure' ? 'procedures' : kind === 'drug' ? 'drugs' : kind === 'wellness' ? 'wellness' : kind === 'mental' ? 'mental-health' : 'traditional-medicine'}`}
                      className="text-sm wofy-link inline-flex items-center gap-0.5">Browse all <ArrowRight className="w-3 h-3"/></Link>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {items.slice(0, 4).map(t => (
                  <Link key={t.slug} to={`/health/topics/${t.kind}/${encodeURIComponent(t.name)}`}
                        className="wofy-card !p-4 hover:shadow-md transition block"
                        data-testid={`topic-${t.slug}`}>
                    <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 mb-1">{t.category || t.kind}</div>
                    <div className="font-medium">{t.name}</div>
                    <div className="text-xs opacity-70 mt-1 inline-flex items-center gap-1"><Sparkles className="w-3 h-3"/>AI-written article on first visit</div>
                  </Link>
                ))}
              </div>
            </section>
          );
        })}

        {/* Trust / safety strip */}
        <FoodPulseHealthStrip limit={16} />

        <section className="wofy-card !p-5 mb-4" style={{ background: "rgba(178,93,56,0.04)" }}>
          <div className="flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 mt-0.5 shrink-0" style={{ color: "var(--wofy-primary)" }}/>
            <div>
              <div className="font-medium mb-1">A note on how we use AI here.</div>
              <p className="text-sm opacity-80">
                Health articles on Social Pulse are written by our editorial AI under strict safety rules — we don't diagnose, prescribe or interpret personal test results. Every article carries an explicit disclaimer and directs urgent symptoms to emergency services. For medical concerns specific to you, always consult a qualified healthcare professional.
              </p>
            </div>
          </div>
        </section>

        {/* Articles of Interest — auto-populates from /api/sp-articles/by-tag */}
        <ArticlesOfInterest
          tags={["health", "healthcare", "medication"]}
          subtitle="Editorial reads on public health, healthcare innovation and medication safety."
          limit={3}
          accent="#0F766E"
          testidPrefix="articles-of-interest-health"
        />
      </div>
    </>
  );
}

function FacilityCard({ f }) {
  return (
    <Link to={`/health/facilities/${f.slug}`}
      className="wofy-card !p-4 hover:shadow-md transition block"
      data-testid={`facility-card-${f.slug}`}>
      <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 mb-1">
        {f.facility_type}{f.category ? ` · ${f.category}` : ""}
      </div>
      <div className="font-medium mb-1 line-clamp-2">{f.name}</div>
      {f.description && <p className="text-sm opacity-80 line-clamp-2">{f.description}</p>}
      {(f.city || f.country) && (
        <div className="text-xs opacity-70 flex items-center gap-1 mt-2">
          <MapPin className="w-3 h-3"/>{[f.city, f.state, f.country].filter(Boolean).join(", ")}
        </div>
      )}
    </Link>
  );
}
